import { canEditAnySubmission, isWithinEditWindow } from '@cult/domain';
import type { PublicPraise } from '@cult/schemas';
import { useNavigate } from '@tanstack/react-router';
import { App, Button, Flex, Popconfirm, Space, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MouseEvent } from 'react';
import { DataTable, DateTimeCell, PersonCell } from '../../../../../components';
import { FORM_ORIGINS } from '../../../../../lib/form-origins';
import { stripMarkdown } from '../../../../../lib/markdown';
import { useCurrentUser } from '../../../../../state/use-current-user';
import { useDeletePraise } from '../../../../praises';
import { FRAMEWORK_TAG_SHAPE, ReferenceTag } from '../../../../reference';

const { Paragraph } = Typography;

type Props = {
  praises: PublicPraise[];
  loading: boolean;
  onOpenPraise: (id: string) => void;
  pagination: TablePaginationConfig;
};

export const AdminPraisesTable = ({ praises, loading, onOpenPraise, pagination }: Props) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const deletePraise = useDeletePraise();
  const role = useCurrentUser((s) => s.user?.role);
  const isAdmin = role !== undefined && canEditAnySubmission(role);

  const handleDelete = (praise: PublicPraise) => {
    deletePraise.mutate(praise, {
      onSuccess: () => message.success('Praise deleted'),
      onError: (err) => message.error(err.message),
    });
  };

  const handleRowClick = (praise: PublicPraise, e: MouseEvent) => {
    // Don't hijack modifier-clicks (so cmd-click stays a plain selection / future deep-link).
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    // Clicks on the Edit/Delete actions (and their popconfirm) shouldn't open the drawer.
    const target = e.target as HTMLElement;
    if (target.closest('.table-row__actions-cell, .ant-popover, .ant-modal')) return;
    onOpenPraise(String(praise.number));
  };

  const columns: ColumnsType<PublicPraise> = [
    {
      title: 'Praise',
      key: 'praise',
      className: 'col-top',
      render: (_v, praise) => (
        <div style={{ minWidth: 0 }}>
          <Paragraph strong ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
            {praise.behavior}
          </Paragraph>
          <Paragraph
            type="secondary"
            ellipsis={{ rows: 2 }}
            style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.5 }}
          >
            {stripMarkdown(praise.whatHappened)}
          </Paragraph>
        </div>
      ),
    },
    {
      title: 'From',
      key: 'author',
      width: 170,
      render: (_v, praise) => <PersonCell user={praise.author} />,
    },
    {
      title: 'To',
      key: 'receiver',
      width: 170,
      render: (_v, praise) => <PersonCell user={praise.receiver} />,
    },
    {
      title: 'Core values',
      key: 'coreValues',
      width: 170,
      render: (_v, praise) => (
        <Flex gap={6} wrap>
          {praise.coreValues.map((value) => (
            <ReferenceTag key={value.id} value={value} />
          ))}
        </Flex>
      ),
    },
    {
      title: 'Frameworks',
      key: 'frameworks',
      width: 170,
      render: (_v, praise) => (
        <Flex gap={6} wrap>
          {praise.frameworks.map((framework) => (
            <ReferenceTag key={framework.id} value={framework} shape={FRAMEWORK_TAG_SHAPE} />
          ))}
        </Flex>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (v: string) => <DateTimeCell value={v} />,
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      className: 'table-row__actions-cell',
      render: (_v, praise) => {
        // Admins edit at any time; delete stays inside the window for everyone.
        const inWindow = isWithinEditWindow(praise.createdAt);
        if (!inWindow && !isAdmin) return null;
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() =>
                navigate({
                  to: '/praises/$praiseId/edit',
                  params: { praiseId: String(praise.number) },
                  search: { origin: FORM_ORIGINS.ADMIN },
                })
              }
            >
              Edit
            </Button>
            {inWindow ? (
              <Popconfirm
                title="Delete this praise?"
                description="This also removes its comments, attachments, and awarded points."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDelete(praise)}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  style={{ padding: 0 }}
                  loading={deletePraise.isPending && deletePraise.variables?.id === praise.id}
                >
                  Delete
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <DataTable<PublicPraise>
      columns={columns}
      dataSource={praises}
      loading={loading}
      pagination={pagination}
      onRow={(praise) => ({
        onClick: (e) => handleRowClick(praise, e),
        className: 'table-row--clickable',
      })}
    />
  );
};
